import React, { useEffect, useMemo, useState } from 'react';
import Tippy from '@tippyjs/react';
import './SessionListPanel.scss';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import {
  buildSessionExplorerListPayload,
  deduplicateSessions,
  formatSessionDateTime,
  formatSessionTime,
  getQuickFilter,
  QuickFilter,
  SessionExplorerAppliedFilters,
  SessionExplorerFacets,
  SessionExplorerListResponse,
  SessionExplorerPagination,
  SessionExplorerSession,
} from '../shared/sessionExplorerList';

interface SessionListPanelProps {
  context: { l2categoryguid?: string };
  onSessionSelect: (session: SessionExplorerSession) => void;
  /** Clears the selected session when the result set changes. */
  onSessionClear: () => void;
  selectedSession?: SessionExplorerSession | null;
}

const DEFAULT_PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_FACETS: SessionExplorerFacets = {
  resolved: { true: 0, false: 0, unknown: 0 },
  sentiment: { positive: 0, neutral: 0, negative: 0 },
  escalated: { true: 0, false: 0 },
};

const EMPTY_PAGINATION: SessionExplorerPagination = {
  page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

const EMPTY_APPLIED_FILTERS: SessionExplorerAppliedFilters = {
  resolved: null,
  sentiment: [],
  agent_ids: [],
  category_ids: [],
  tags: [],
  is_escalated: null,
  search: null,
  page: 1,
  per_page: DEFAULT_PAGE_SIZE,
};

function getSentimentClass(session: SessionExplorerSession): string {
  return `ca-session-list__sentiment--${session.sentiment.label}`;
}

function getSentimentTooltip(session: SessionExplorerSession): string {
  const label = session.sentiment.label.replace(/^\w/, (letter) => letter.toUpperCase());
  return session.sentiment.score === null
    ? `Sentiment: ${label}`
    : `Sentiment: ${label} (${session.sentiment.score.toFixed(2)})`;
}

function getResolutionLabel(resolved: boolean | null): string {
  if (resolved === true) return 'Resolved';
  if (resolved === false) return 'Unresolved';
  return 'Unknown';
}

function getResolutionClass(resolved: boolean | null): string {
  if (resolved === true) return 'ca-session-list__status--resolved';
  if (resolved === false) return 'ca-session-list__status--unresolved';
  return 'ca-session-list__status--unknown';
}

const SessionListPanel: React.FC<SessionListPanelProps> = ({
  context,
  onSessionSelect,
  onSessionClear,
  selectedSession,
}) => {
  const { endpoints, dashboardConfig, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dashboardConfig: state.configDashboard,
    dateFilter: state.dateFilter,
  }));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<SessionExplorerSession[]>([]);
  const [pagination, setPagination] = useState<SessionExplorerPagination>(EMPTY_PAGINATION);
  const [facets, setFacets] = useState<SessionExplorerFacets>(EMPTY_FACETS);
  const [appliedFilters, setAppliedFilters] =
    useState<SessionExplorerAppliedFilters>(EMPTY_APPLIED_FILTERS);
  const { response, axiosFetch, loading, error } = useAxios();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    onSessionClear();
    setRows([]);
    setPagination(EMPTY_PAGINATION);
    setFacets(EMPTY_FACETS);
    setAppliedFilters(EMPTY_APPLIED_FILTERS);
  }, [context.l2categoryguid, dateFilter, onSessionClear, page, quickFilter, search]);

  useEffect(() => {
    if (!dateFilter) return;
    getDataFromSingleEndpoint(
      endpoints.getWidgetData,
      buildSessionExplorerListPayload(
        dateFilter,
        dashboardConfig.external,
        page,
        DEFAULT_PAGE_SIZE,
        search,
        quickFilter,
        context.l2categoryguid
      ),
      axiosFetch
    );
  }, [
    context.l2categoryguid,
    dashboardConfig.external,
    dateFilter,
    endpoints.getWidgetData,
    page,
    quickFilter,
    search,
  ]);

  useEffect(() => {
    const data = (response as SessionExplorerListResponse | undefined)?.data;
    if (!data) return;
    setRows(deduplicateSessions(data.rows));
    setPagination(data.pagination);
    setFacets(data.facets);
    setAppliedFilters(data.applied_filters);
  }, [response]);

  const activeQuickFilter = useMemo(() => getQuickFilter(appliedFilters), [appliedFilters]);
  const rangeLabel =
    pagination.total === 0
      ? '0 of 0'
      : `${(pagination.page - 1) * pagination.per_page + 1}-${Math.min(
          pagination.page * pagination.per_page,
          pagination.total
        )} of ${pagination.total.toLocaleString()}`;
  const quickFilters: Array<{ id: QuickFilter; label: string; count?: number }> = [
    { id: 'all', label: 'All', count: pagination.total },
    { id: 'unresolved', label: 'Unresolved', count: facets.resolved.false },
    { id: 'negative', label: 'Negative', count: facets.sentiment.negative },
    { id: 'escalated', label: 'Escalated', count: facets.escalated.true },
  ];

  const selectQuickFilter = (filter: QuickFilter) => {
    setQuickFilter(filter);
    setPage(1);
  };

  return (
    <aside className="ca-session-list" aria-label="Sessions">
      <div className="ca-session-list__header">
        <span className="ca-session-list__title">Sessions</span>
        <span className="ca-session-list__count">{rangeLabel}</span>
      </div>

      <div className="ca-session-list__search-wrap">
        <span className="material-icons ca-session-list__search-icon" aria-hidden="true">
          search
        </span>
        <input
          className="ca-session-list__search"
          type="search"
          placeholder="Search reason, agent, ID..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          aria-label="Search reason, agent, ID"
        />
      </div>

      <div className="ca-session-list__quick-filters" role="group" aria-label="Quick filters">
        {quickFilters.map(({ id, label, count }) => (
          <button
            key={id}
            className={`ca-session-list__quick-filter${
              activeQuickFilter === id ? ' ca-session-list__quick-filter--active' : ''
            }`}
            onClick={() => selectQuickFilter(id)}
            type="button"
            aria-pressed={activeQuickFilter === id}
          >
            {label} <span className="ca-session-list__quick-filter-count">({count ?? 0})</span>
          </button>
        ))}
      </div>

      {appliedFilters.search && (
        <div className="ca-session-list__applied-filters" aria-label="Applied filters">
          <span className="ca-session-list__applied-filter">Search: {appliedFilters.search}</span>
        </div>
      )}

      <div className="ca-session-list__results">
        {loading && (
          <div className="ca-session-list__loading" aria-label="Loading sessions">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="ca-session-list__skeleton" />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="ca-session-list__error" role="alert">
            <span className="material-icons" aria-hidden="true">
              error_outline
            </span>
            Failed to load session data. Please try again later.
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="ca-session-list__no-data">
            <span className="material-icons" aria-hidden="true">
              inbox
            </span>
            No sessions found
          </div>
        )}

        {!loading &&
          !error &&
          rows.map((session) => {
            const primaryResponder = session.agents[0];
            const primaryResponderName = primaryResponder?.name ?? 'No responder recorded';
            const primaryResponderIcon =
              primaryResponder?.emitter_role === 'Agent'
                ? 'headset_mic'
                : primaryResponder?.emitter_role === 'Bot'
                ? 'smart_toy'
                : 'person_outline';
            const selected = selectedSession?.session_id === session.session_id;
            return (
              <button
                key={session.session_id}
                className={`ca-session-list__card${
                  selected ? ' ca-session-list__card--selected' : ''
                }`}
                onClick={() => onSessionSelect(session)}
                type="button"
                aria-pressed={selected}
              >
                <div className="ca-session-list__card-meta">
                  <Tippy
                    className="ca-session-list-tooltip"
                    content={getSentimentTooltip(session)}
                    placement="top"
                    zIndex={100000}
                  >
                    <span
                      className={`ca-session-list__sentiment ${getSentimentClass(session)}`}
                      aria-label={getSentimentTooltip(session)}
                      role="img"
                    />
                  </Tippy>
                  <span className="ca-session-list__session-id">{session.session_id}</span>
                  <Tippy
                    className="ca-session-list-tooltip"
                    content={formatSessionDateTime(session.start_datetime)}
                    placement="top"
                    zIndex={100000}
                  >
                    <span className="ca-session-list__time">
                      {formatSessionTime(session.start_datetime)}
                    </span>
                  </Tippy>
                </div>
                <span className="ca-session-list__contact-reason">
                  {session.contact_reason ?? 'No contact reason recorded'}
                </span>
                <div className="ca-session-list__card-footer">
                  <span className="ca-session-list__agent">
                    <span className="material-icons" aria-hidden="true">
                      {primaryResponderIcon}
                    </span>
                    {primaryResponderName}
                  </span>
                  <span className="ca-session-list__separator" aria-hidden="true">
                    -
                  </span>
                  <span>{session.message_count ?? 0} msgs</span>
                  <span className="ca-session-list__state-indicators">
                    {session.is_escalated && (
                      <Tippy
                        className="ca-session-list-tooltip"
                        content="Escalated"
                        placement="top"
                        zIndex={100000}
                      >
                        <span
                          className="ca-session-list__escalated"
                          aria-label="Escalated"
                          role="img"
                        >
                          !
                        </span>
                      </Tippy>
                    )}
                    <span
                      className={`ca-session-list__status ${getResolutionClass(session.resolved)}`}
                    >
                      {getResolutionLabel(session.resolved)}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
      </div>

      <div className="ca-session-list__pagination">
        <button
          className="ca-session-list__page-btn"
          onClick={() => setPage((currentPage) => currentPage - 1)}
          disabled={!pagination.has_prev || loading}
          type="button"
          aria-label="Previous page"
        >
          <span className="material-icons" aria-hidden="true">
            chevron_left
          </span>
        </button>
        <span className="ca-session-list__page-label">
          Page {pagination.page} of {pagination.total_pages || 1}
        </span>
        <button
          className="ca-session-list__page-btn"
          onClick={() => setPage((currentPage) => currentPage + 1)}
          disabled={!pagination.has_next || loading}
          type="button"
          aria-label="Next page"
        >
          <span className="material-icons" aria-hidden="true">
            chevron_right
          </span>
        </button>
      </div>
    </aside>
  );
};

export default SessionListPanel;
